// --- File: ./TelegramConsentPage.tsx ---
// Frontend React component to display consent and handle submission for mock or real flow.
// Handles JSON response from the mock backend's /mock_auth/complete endpoint.

import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom"; // Assuming React Router v5/v6
import { Container } from "@/components/Container"; // Adjust import path as needed
import { Button } from "@/components/ui/button"; // Adjust import path as needed
import {
  LoaderCircle,
  CheckCircle,
  ShieldCheck,
  Ban,
  XCircle,
} from "lucide-react";
import { apiClient } from "@/api/api"; // Adjust import path, ensure apiClient is configured with backend base URL

// --- Interface Definitions ---
interface OAuthParams {
  client_id: string | null;
  scope: string | null;
  state: string | null; // Original bot state (used by real flow, potentially mock)
  mock_state: string | null; // State specifically for the mock flow completion
  redirect_uri: string | null; // Used by real flow
  response_type: string | null;
  client_name: string;
  isMockFlow: boolean; // Flag to determine flow type
}

interface ConsentResponse {
  status: "success" | "cancelled" | "error";
  message: string;
}

// --- Scope Descriptions (Example) ---
const SCOPE_DESCRIPTIONS: Record<string, string> = {
  openid: "Verify your identity.",
  profile: "Access your basic profile information.",
  email: "Access your registered email address.",
  "read:balance": "View your account balance.",
  "write:bets": "Place bets on your behalf.",
  // Add other scope descriptions as needed
};

// --- Helper/dummy logger if not globally available ---
const logger = {
  debug: (...args: any[]) => console.debug("DEBUG:", ...args),
  info: (...args: any[]) => console.info("INFO:", ...args),
  warn: (...args: any[]) => console.warn("WARN:", ...args),
  error: (...args: any[]) => console.error("ERROR:", ...args),
};

// --- Component ---
export function TelegramConsentPage() {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [pageState, setPageState] = useState<"loading" | "consent" | "result">(
    "loading",
  );
  const [result, setResult] = useState<ConsentResponse | null>(null); // Stores the final result from backend
  const [oauthParams, setOAuthParams] = useState<OAuthParams | null>(null);
  const [displayError, setDisplayError] = useState<string | null>(null); // For initial load errors

  // Parse URL parameters on component mount
  useEffect(() => {
    logger.debug("Consent Page useEffect running...");
    const searchParams = new URLSearchParams(location.search);
    const isMock = searchParams.get("mock") === "true";
    const mockStateParam = searchParams.get("mock_state"); // Key param for mock flow completion
    const stateParam = searchParams.get("state"); // Original bot state

    logger.debug(
      `Parsed URL params: isMock=${isMock}, mock_state=${mockStateParam}, state=${stateParam}`,
    );

    const params: Partial<OAuthParams> = {
      client_id: searchParams.get("client_id"),
      scope: searchParams.get("scope"),
      state: stateParam,
      mock_state: mockStateParam,
      redirect_uri: searchParams.get("redirect_uri"),
      response_type: searchParams.get("response_type"),
      client_name:
        searchParams.get("client_name") ||
        (isMock ? "Mock Linked App" : "Telegram Bot V2"), // Adjust default names
      isMockFlow: isMock,
    };

    // Basic validation
    let isValid = true;
    let errorMsg = "";
    if (isMock && !params.mock_state) {
      isValid = false;
      errorMsg =
        "Invalid mock authorization request: Missing mock state identifier.";
    } else if (!isMock && !params.state) {
      // Real flow requires original state
      isValid = false;
      errorMsg = "Invalid authorization request: Missing state identifier.";
    }
    // Add checks for other required params if needed, especially for real flow

    if (!isValid) {
      logger.error(errorMsg);
      setDisplayError(errorMsg + " Please try linking from Telegram again.");
      setPageState("result");
      setResult({ status: "error", message: "Invalid request link." });
      setOAuthParams(null);
    } else {
      logger.debug("Setting valid OAuth params:", params);
      setOAuthParams(params as OAuthParams);
      setPageState("consent");
      setDisplayError(null);
    }
  }, [location.search]);

  // Handle form submission (Allow/Deny)
  const handleConsentSubmit = useCallback(
    async (consentDecision: "yes" | "no") => {
      // Ensure params are loaded and have the required state for the current flow
      if (
        !oauthParams ||
        (oauthParams.isMockFlow && !oauthParams.mock_state) ||
        (!oauthParams.isMockFlow && !oauthParams.state)
      ) {
        setDisplayError("Cannot process consent: Invalid request parameters.");
        logger.error(
          "handleConsentSubmit called with invalid oauthParams:",
          oauthParams,
        );
        return;
      }
      const isMockFlow = oauthParams.isMockFlow; // Get flow type from state

      setIsLoading(true);
      setDisplayError(null);

      // --- Prepare Form Data ---
      const formData = new URLSearchParams();
      formData.append("confirm", consentDecision);

      // Append appropriate state based on flow
      if (isMockFlow) {
        formData.append("mock_state", oauthParams.mock_state!); // Use mock_state for mock flow
      } else {
        formData.append("state", oauthParams.state!); // Use original bot state for real flow
      }

      // Include other standard OAuth params (backend might ignore them in mock, but needed for real)
      if (oauthParams.client_id)
        formData.append("client_id", oauthParams.client_id);
      if (oauthParams.scope) formData.append("scope", oauthParams.scope);
      if (oauthParams.redirect_uri)
        formData.append("redirect_uri", oauthParams.redirect_uri);
      if (oauthParams.response_type)
        formData.append("response_type", oauthParams.response_type);
      // Include original state in mock request body if backend needs it (optional)
      if (isMockFlow && oauthParams.state) {
        formData.append("state", oauthParams.state);
      }

      // --- Determine Target Endpoint ---
      const targetEndpoint = isMockFlow
        ? "/mock_auth/complete" // Mock backend endpoint
        : "/api/oauth/authorize"; // Real backend endpoint (adjust if needed)

      logger.info(
        `Submitting consent (${consentDecision}) to: ${targetEndpoint} with state: ${isMockFlow ? oauthParams.mock_state : oauthParams.state}`,
      );

      try {
        // --- Make API Call ---
        const response = await apiClient.post<ConsentResponse | any>(
          targetEndpoint,
          formData.toString(), // Send as x-www-form-urlencoded
          {
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            maxRedirects: 0, // Prevent Axios from following redirects (important for real flow)
            validateStatus: (status) => status >= 200 && status < 500, // Process 2xx, 3xx, 4xx responses
          },
        );

        logger.debug(
          "Consent POST response status:",
          response.status,
          "Data:",
          response.data,
        );

        // --- Process Response ---
        if (isMockFlow) {
          // Mock Flow: Expect JSON response {status, message}
          if (
            response.status >= 200 &&
            response.status < 300 &&
            response.data?.status
          ) {
            setResult(response.data as ConsentResponse); // Store the result {status, message}
          } else {
            // Handle unexpected response or error status from mock backend
            logger.error(
              "Unexpected mock response:",
              response.status,
              response.data,
            );
            const errorDetail =
              typeof response.data === "object"
                ? response.data?.message || response.data?.error
                : response.data;
            setResult({
              status: "error",
              message:
                errorDetail || `Request failed with status ${response.status}.`,
            });
          }
          setPageState("result"); // Show the result page (success/error/cancelled)
        } else {
          // Real OAuth Flow: Expect 3xx redirect from backend
          if (
            response.status >= 300 &&
            response.status < 400 &&
            response.headers.location
          ) {
            // Browser should handle redirect automatically based on Location header.
            // If it sometimes doesn't (e.g., SPA interference), uncommenting below might help, but usually isn't needed.
            logger.info(
              "Real OAuth flow redirecting via Location header:",
              response.headers.location,
            );
            // window.location.href = response.headers.location;
          } else {
            // Real OAuth flow failed (e.g., 400, 401, 500 from backend)
            logger.error(
              "Real OAuth POST failed:",
              response.status,
              response.data,
            );
            const errorMsg =
              response.data?.detail ||
              response.data?.message ||
              `Authorization failed (Status: ${response.status})`;
            setResult({ status: "error", message: errorMsg });
            setPageState("result"); // Show result page with error
          }
        }
      } catch (err: any) {
        // Network error or other exception during apiClient call
        logger.error("Error submitting consent via apiClient:", err);
        let errorMessage = "An unexpected error occurred.";
        if (err.message) {
          errorMessage = `Network error or client-side issue: ${err.message}`;
        }
        setResult({ status: "error", message: errorMessage });
        setPageState("result");
      } finally {
        setIsLoading(false); // Stop loading indicator
      }
    },
    [oauthParams],
  ); // Dependency only on oauthParams state

  // --- Render Logic ---

  // 1. Loading State
  if (pageState === "loading") {
    return (
      <Container className="pt-16 pb-10 text-center">
        <LoaderCircle className="h-12 w-12 animate-spin text-primary mx-auto" />
        <p className="mt-4 text-white/70">Loading authorization request...</p>
      </Container>
    );
  }

  // 2. Result State (Success, Cancelled, Error) - Displayed after submission
  if (pageState === "result" && result) {
    const isError = result.status === "error";
    const isCancelled = result.status === "cancelled";
    const Icon = isError ? XCircle : isCancelled ? Ban : CheckCircle;
    const colorClass = isError
      ? "text-red-400"
      : isCancelled
        ? "text-yellow-400"
        : "text-green-400";
    const borderClass = isError
      ? "border-red-600/50"
      : isCancelled
        ? "border-yellow-600/50"
        : "border-green-600/50";
    const bgClass = isError
      ? "bg-red-900/30"
      : isCancelled
        ? "bg-yellow-900/30"
        : "bg-green-900/30";
    const titleText = isError
      ? "Authorization Error"
      : isCancelled
        ? "Authorization Cancelled"
        : "Authorization Complete";

    return (
      <Container className="pt-10 pb-10 text-center">
        <div
          className={`${bgClass} border ${borderClass} rounded-lg p-6 max-w-md mx-auto`}
        >
          <Icon className={`w-12 h-12 mb-4 mx-auto ${colorClass}`} />
          <h1 className={`text-xl font-semibold mb-2 ${colorClass}`}>
            {titleText}
          </h1>
          <p className="text-white/80 mb-4">{result.message}</p>
          {/* Display initial load error if it occurred before result */}
          {displayError && (
            <p className="text-red-500 text-sm mt-2">
              Initial Error: {displayError}
            </p>
          )}
          <p className="text-sm text-white/60 mt-6">
            You can now close this window.
          </p>
        </div>
      </Container>
    );
  }

  // 3. Consent State (Initial display before user action)
  if (pageState === "consent" && oauthParams) {
    const scopes = oauthParams.scope ? oauthParams.scope.split(" ") : [];
    return (
      <Container className="pt-10 pb-10">
        <div className="max-w-lg mx-auto bg-black/40 border border-white/10 rounded-lg p-6 md:p-8 shadow-lg">
          <div className="text-center mb-6">
            <ShieldCheck className="h-12 w-12 mx-auto text-primary mb-3" />
            <h1 className="text-2xl font-semibold text-white">
              Authorize Application
            </h1>
          </div>
          <p className="text-center text-white/80 mb-6 text-lg">
            The application{" "}
            <strong className="text-white">{oauthParams.client_name}</strong>{" "}
            wants to access your account.
            {oauthParams.isMockFlow && (
              <span className="block text-sm text-yellow-400">
                (Mock Mode Active)
              </span>
            )}
          </p>
          <div className="mb-6 border-t border-b border-white/10 py-4">
            <p className="font-semibold text-white/90 mb-2">
              It requests the following permissions:
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-1 pl-2">
              {scopes.length > 0 ? (
                scopes.map((scope) => (
                  <li key={scope}>
                    {SCOPE_DESCRIPTIONS[scope] ||
                      `Perform actions related to: ${scope}`}
                  </li>
                ))
              ) : (
                <li>Basic access requested.</li>
              )}
            </ul>
          </div>

          {/* Display submission errors if any */}
          {displayError && (
            <p className="text-red-400 text-center mb-4">{displayError}</p>
          )}

          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-6">
            <Button
              variant="outline"
              onClick={() => handleConsentSubmit("no")}
              disabled={isLoading}
              className="border-red-500/50 text-red-400 hover:bg-red-900/30 hover:text-red-300 w-full sm:w-auto"
              size="lg"
            >
              {isLoading ? (
                <LoaderCircle className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Ban className="w-5 h-5 mr-2" />
              )}{" "}
              Deny
            </Button>
            <Button
              onClick={() => handleConsentSubmit("yes")}
              disabled={isLoading}
              className="bg-[#DFFE00] hover:bg-[#DFFE00]/90 text-black font-medium px-8 py-3 w-full sm:w-auto"
              size="lg"
            >
              {isLoading ? (
                <LoaderCircle className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-5 h-5 mr-2" />
              )}{" "}
              Allow
            </Button>
          </div>
          <p className="text-xs text-center text-white/50 mt-8">
            By clicking Allow, you permit this application to access your data
            as described. You can revoke permissions later.
          </p>
        </div>
      </Container>
    );
  }

  // Fallback if none of the states match (e.g., initial render before useEffect finishes)
  return (
    <Container className="pt-16 pb-10 text-center">
      <p className="text-white/70">Initializing...</p>
    </Container>
  );
}
